// v2
// Path: frontend/src/components/Dashboard.jsx
// Inputs: user, token, onLogout
// Outputs: Master dashboard layout and tool switcher
// Summary: Main administrator portal container rendering navigation sidebar and tool components.

import React, { useState, useEffect } from 'react';
import { DatabaseAdmin } from './DatabaseAdmin.jsx';
import { DatabaseAdminV2 } from './DatabaseAdminV2.jsx';
import { RemoteSQLExplorer } from './RemoteSQLExplorer.jsx';
import { InventoryDeployment } from './InventoryDeployment.jsx';
import QuickBooksSync from './QuickBooksSync/index.jsx';
import DataCanvas from './DataCanvasV3';

export const Dashboard = ({ user, token, onLogout }) => {
    const [view, setView] = useState(null);
    const [tools, setTools] = useState([]);

    useEffect(() => {
        const fetchTools = async () => {
            try {
                const res = await fetch('/api/data/tools', { headers: { 'Authorization': `Bearer ${token}` } });
                if (res.ok) {
                    let dbTools = await res.json();
                    dbTools = dbTools.map(t => {
                        if (t.RouteIdentifier === 'db-admin') {
                            return { ...t, RouteIdentifier: '/SapphireOps', ToolName: 'SapphireOps (Local)' };
                        }
                        return t;
                    });
                    if (!dbTools.some(t => t.RouteIdentifier === 'quickbooks-sync')) {
                        dbTools.push({
                            ToolName: 'QuickBooks Integration & Sync',
                            RouteIdentifier: 'quickbooks-sync',
                            MaterialSymbolName: 'sync_alt'
                        });
                    }
                    dbTools.push({
                        ToolName: 'Inventory Deployment',
                        RouteIdentifier: 'inventory-deploy',
                        MaterialSymbolName: 'deployed_code'
                    });
                    dbTools.push({
                        ToolName: 'Database Administration (Deprecated)',
                        RouteIdentifier: 'db-admin-legacy',
                        MaterialSymbolName: 'warning'
                    });
                    dbTools.push({
                        ToolName: 'V3 Data Canvas (Beta)',
                        RouteIdentifier: 'v3-canvas',
                        MaterialSymbolName: 'explore'
                    });
                    setTools(dbTools);
                }
            } catch (err) {
                console.error("Failed to load admin tools", err);
            }
        };
        fetchTools();
    }, [token]);

    const renderTool = () => {
        switch (view) {
            case 'quickbooks-sync':
                return <QuickBooksSync token={token} />;
            case 'inventory-deploy':
                return <InventoryDeployment token={token} />;
            case '/SapphireOps':
                return <DatabaseAdminV2 token={token} />;
            case 'db-admin-legacy':
                return <DatabaseAdmin token={token} />;
            case '/remote-sql':
                return <RemoteSQLExplorer token={token} />;
            case 'v3-canvas':
                return <DataCanvas defaultViewId={null} token={token} />;
            default:
                return <div className="text-slate-400">Tool module not yet implemented.</div>;
        }
    };

    const currentToolName = tools.find(t => t.RouteIdentifier === view)?.ToolName || 'Command Center';

    return (
        <div className="flex h-screen bg-slate-900 text-slate-200 overflow-hidden">
            <aside className="w-64 bg-slate-800 border-r border-slate-700 flex flex-col shadow-xl z-10">
                <div className="h-16 flex items-center px-6 border-b border-slate-700 bg-slate-900/50">
                    <span className="text-xl font-bold text-blue-500 tracking-wider">SAPPHIRE</span>
                </div>
                <div className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Admin Tools
                </div>
                <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
                    {tools.map((tool) => (
                        <button key={tool.RouteIdentifier} onClick={() => setView(tool.RouteIdentifier)} 
                            className={`w-full text-left px-3 py-2.5 rounded-md transition-colors flex items-center ${view === tool.RouteIdentifier ? 'bg-blue-600/20 text-blue-400 font-medium border border-blue-500/20' : 'hover:bg-slate-700/50 text-slate-300'}`}>
                            <span className="material-symbols-outlined mr-3 text-[20px]">{tool.MaterialSymbolName}</span> 
                            {tool.ToolName}
                        </button>
                    ))}
                </nav>
                <div className="p-4 border-t border-slate-700 bg-slate-800/80">
                    <div className="text-sm font-medium text-slate-300 mb-1 flex items-center">
                        <span className="material-symbols-outlined mr-2 text-[18px]">account_circle</span>
                        {user?.name}
                    </div>
                    <button onClick={onLogout} className="w-full flex items-center px-3 py-2 rounded-md hover:bg-red-500/10 text-red-400 text-sm transition-colors mt-2 border border-transparent hover:border-red-500/20">
                        <span className="material-symbols-outlined mr-2 text-[18px]">logout</span>
                        Terminate Session
                    </button>
                </div>
            </aside>
            
            <main className="flex-1 flex flex-col bg-[#0f172a] min-w-0">
                <header className="h-16 bg-slate-900 border-b border-slate-800 flex items-center px-8 shadow-sm">
                    <h2 className="text-lg font-medium text-slate-200">{currentToolName}</h2>
                </header>
                <div className="p-8 flex-1 overflow-hidden flex flex-col min-w-0">
                    {renderTool()}
                </div>
            </main>
        </div>
    );
};